/**
 * Meta Pixel (Facebook Pixel) Context
 * 
 * Handles client-side Meta Pixel tracking with:
 * - Automatic PageView tracking
 * - Standard events (Lead, CompleteRegistration, Purchase)
 * - Event deduplication with backend CAPI (via event_id)
 * - Cookie capture for fbc/fbp
 * - Consent awareness (GDPR compliant)
 * 
 * Usage:
 *   import { useMetaPixel } from '../contexts/MetaPixelContext';
 *   const { trackLead, trackPurchase } = useMetaPixel();
 */
import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useConsent } from './ConsentContext';

// Get Pixel ID from environment
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

// Meta Pixel Context
const MetaPixelContext = createContext(null);

/**
 * Generate unique event ID for deduplication with CAPI
 */
const generateEventId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Get Facebook cookies for advanced matching
 */
const getFacebookCookies = () => {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
    }, {});

    return {
        fbc: cookies._fbc || null,
        fbp: cookies._fbp || null,
    };
};

/**
 * Normalize phone number for Advanced Matching
 * Meta expects: digits only, country code included
 */
const normalizePhone = (phone) => {
    if (!phone) return null;
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Ensure it starts with country code (assume missing means needs one)
    return digits.length >= 10 ? digits : null;
};

/**
 * Meta Pixel Provider Component
 */
export const MetaPixelProvider = ({ children }) => {
    const { hasConsent, consentRequired } = useConsent();
    const [isInitialized, setIsInitialized] = useState(false);

    // Check if tracking is allowed
    const canTrack = !consentRequired || hasConsent;

    // Initialize Pixel on mount (only if consent granted)
    useEffect(() => {
        if (!PIXEL_ID) {
            console.warn('Meta Pixel ID not configured - tracking disabled');
            return;
        }

        // Wait for consent if required
        if (consentRequired && !hasConsent) {
            console.log('🔵 Meta Pixel waiting for consent...');
            return;
        }

        // Check if already initialized
        if (window.fbq) {
            return;
        }

        // Facebook Pixel Base Code
        (function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = '2.0';
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        // Initialize with Pixel ID (no user data initially)
        window.fbq('init', PIXEL_ID);

        // Track initial PageView
        window.fbq('track', 'PageView');

        console.log('🔵 Meta Pixel initialized:', PIXEL_ID);
        setIsInitialized(true);
    }, [hasConsent, consentRequired]);

    /**
     * Set Advanced Matching data for better conversion attribution
     * Call this when user logs in or provides their information
     * Meta automatically hashes this data before sending
     * 
     * @param {Object} userData - User data for matching
     * @param {string} userData.email - User email
     * @param {string} userData.phone - User phone (with country code)
     * @param {string} userData.firstName - First name
     * @param {string} userData.lastName - Last name
     * @param {string} userData.city - City
     * @param {string} userData.state - State/Province
     * @param {string} userData.zipCode - ZIP/Postal code
     * @param {string} userData.country - Country code (2-letter)
     * @param {string} userData.externalId - Your internal user ID
     */
    const setAdvancedMatchingData = useCallback((userData) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return;

        const advancedMatchingData = {};

        // Email (Meta hashes automatically)
        if (userData.email) {
            advancedMatchingData.em = userData.email.toLowerCase().trim();
        }

        // Phone number (digits only, with country code)
        if (userData.phone) {
            const normalizedPhone = normalizePhone(userData.phone);
            if (normalizedPhone) {
                advancedMatchingData.ph = normalizedPhone;
            }
        }

        // Name
        if (userData.firstName) {
            advancedMatchingData.fn = userData.firstName.toLowerCase().trim();
        }
        if (userData.lastName) {
            advancedMatchingData.ln = userData.lastName.toLowerCase().trim();
        }

        // Location
        if (userData.city) {
            advancedMatchingData.ct = userData.city.toLowerCase().trim().replace(/\s/g, '');
        }
        if (userData.state) {
            advancedMatchingData.st = userData.state.toLowerCase().trim();
        }
        if (userData.zipCode) {
            advancedMatchingData.zp = userData.zipCode.trim();
        }
        if (userData.country) {
            advancedMatchingData.country = userData.country.toLowerCase().trim();
        }

        // External ID (your user ID)
        if (userData.externalId) {
            advancedMatchingData.external_id = userData.externalId;
        }

        // Re-init pixel with Advanced Matching data
        if (Object.keys(advancedMatchingData).length > 0) {
            window.fbq('init', PIXEL_ID, advancedMatchingData);
            console.log('🔵 Meta Pixel: Advanced Matching data set', Object.keys(advancedMatchingData));
        }
    }, [canTrack]);

    // Track PageView (for SPA navigation)
    const trackPageView = useCallback(() => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return;
        window.fbq('track', 'PageView');
    }, [canTrack]);

    /**
     * Track ViewContent event for page attribution
     */
    const trackViewContent = useCallback(({ contentName, contentCategory, contentType = 'page' } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = generateEventId();

        window.fbq('track', 'ViewContent', {
            content_name: contentName,
            content_category: contentCategory,
            content_type: contentType,
        }, { eventID: eid });

        console.log('🔵 Meta Pixel: ViewContent tracked', { contentName, contentCategory });
        return eid;
    }, [canTrack]);

    /**
     * Track Lead event (trial activation, contact form)
     * @param {Object} params - Lead parameters
     * @param {string} params.eventId - Event ID for CAPI deduplication
     * @param {string} params.contentName - Lead type (e.g., "trial", "contact")
     * @param {string} params.email - User email (optional, for advanced matching)
     */
    const trackLead = useCallback(({ eventId, contentName = 'trial', email = null } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        const params = {
            content_name: contentName,
            content_category: 'subscription',
        };

        // Track with event ID for deduplication
        window.fbq('track', 'Lead', params, { eventID: eid });

        console.log('🔵 Meta Pixel: Lead tracked', { eventId: eid, contentName });

        return eid;
    }, [canTrack]);

    /**
     * Track CompleteRegistration event
     * @param {Object} params - Registration parameters
     * @param {string} params.eventId - Event ID for CAPI deduplication
     * @param {string} params.email - User email
     */
    const trackCompleteRegistration = useCallback(({ eventId, email = null } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        const params = {
            content_name: 'User Registration',
            status: 'completed',
        };

        window.fbq('track', 'CompleteRegistration', params, { eventID: eid });

        console.log('🔵 Meta Pixel: CompleteRegistration tracked', { eventId: eid });

        return eid;
    }, [canTrack]);

    /**
     * Track Purchase event
     * @param {Object} params - Purchase parameters
     * @param {string} params.eventId - Event ID for CAPI deduplication
     * @param {number} params.value - Purchase value
     * @param {string} params.currency - Currency code (default: USD)
     * @param {string} params.contentName - Product name
     */
    const trackPurchase = useCallback(({
        eventId,
        value,
        currency = 'USD',
        contentName = 'Subscription',
        orderId = null,
    } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        const params = {
            value: value,
            currency: currency,
            content_name: contentName,
            content_type: 'product',
            num_items: 1,
        };

        if (orderId) {
            params.order_id = orderId;
        }

        window.fbq('track', 'Purchase', params, { eventID: eid });

        console.log('🔵 Meta Pixel: Purchase tracked', { eventId: eid, value, currency });

        return eid;
    }, [canTrack]);

    /**
     * Track Subscribe event
     * @param {Object} params - Subscription parameters
     */
    const trackSubscribe = useCallback(({ eventId, value = 0, currency = 'USD' } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        window.fbq('track', 'Subscribe', {
            value: value,
            currency: currency,
        }, { eventID: eid });

        console.log('🔵 Meta Pixel: Subscribe tracked', { eventId: eid });

        return eid;
    }, [canTrack]);

    /**
     * Track StartTrial event
     * @param {Object} params - Trial parameters
     */
    const trackStartTrial = useCallback(({ eventId, predictedLtv = 49.00 } = {}) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        window.fbq('track', 'StartTrial', {
            value: 0,
            currency: 'USD',
            predicted_ltv: predictedLtv,
        }, { eventID: eid });

        console.log('🔵 Meta Pixel: StartTrial tracked', { eventId: eid });

        return eid;
    }, [canTrack]);

    /**
     * Track custom event
     */
    const trackCustom = useCallback((eventName, params = {}, eventId = null) => {
        if (!canTrack || !window.fbq || !PIXEL_ID) return null;

        const eid = eventId || generateEventId();

        window.fbq('trackCustom', eventName, params, { eventID: eid });

        return eid;
    }, [canTrack]);

    const value = {
        isEnabled: !!PIXEL_ID && canTrack,
        isInitialized,
        pixelId: PIXEL_ID,
        setAdvancedMatchingData,
        trackPageView,
        trackViewContent,
        trackLead,
        trackCompleteRegistration,
        trackPurchase,
        trackSubscribe,
        trackStartTrial,
        trackCustom,
        getFacebookCookies,
        generateEventId,
    };

    return (
        <MetaPixelContext.Provider value={value}>
            {children}
        </MetaPixelContext.Provider>
    );
};

/**
 * Hook to access Meta Pixel methods
 */
export const useMetaPixel = () => {
    const context = useContext(MetaPixelContext);

    if (!context) {
        // Return no-op functions if not in provider
        return {
            isEnabled: false,
            isInitialized: false,
            setAdvancedMatchingData: () => { },
            trackPageView: () => { },
            trackViewContent: () => null,
            trackLead: () => null,
            trackCompleteRegistration: () => null,
            trackPurchase: () => null,
            trackSubscribe: () => null,
            trackStartTrial: () => null,
            trackCustom: () => null,
            getFacebookCookies: () => ({ fbc: null, fbp: null }),
            generateEventId: () => null,
        };
    }

    return context;
};

export default MetaPixelContext;
